import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronsRight, Plus, X } from 'lucide-react';
import { createModal } from 'react-modal-promise';
import GeneralModal from '../../helpers/GeneralModal';
import LogicContext from '../../Context/LogicContext';
import { budgetService } from '../../services/budgetService';
import { formatCustomNumber } from '../../helpers/formatCustomNumber';
import DelgroProgressBar from '../../generals/DelgroProgressBar';
import { buildBudgetXml, categorizeAccount, getBudgetPeriods } from './budgetDataHelpers';
import AccountPickerModalShared from './AccountPickerModal';


const inputCls =
  'w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition';
const selectCls = `${inputCls} appearance-none`;

// income and expense are always budgeted, the rest are opt-in (see the toggle)
const CORE_SECTIONS = [
  { key: 'income', title: 'Income Accounts' },
  { key: 'expense', title: 'Expense Accounts' },
];

const EXTRA_SECTIONS = [
  { key: 'asset', title: 'Asset Accounts' },
  { key: 'liability', title: 'Liability Accounts' },
  { key: 'equity', title: 'Equity Accounts' },
  { key: 'other', title: 'Other Accounts' },
];


function normalizeMasterData(serverData) {
  const data = Array.isArray(serverData) ? serverData[0] : serverData;
  return {
    account_head_info: data?.account_head_info || [],
    financial_year: data?.financial_year || [],
    budget_term: data?.budget_term || [],
  };
}


/* ------------------------------------------------------------------ page -- */

function AddBudgetPagePage() {
  const navigate = useNavigate();
  const logicContext = useContext(LogicContext);
  const myPromiseModal = createModal(GeneralModal);

  const [masterData, setMasterData] = useState({
    account_head_info: [],
    financial_year: [],
    budget_term: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [budgetName, setBudgetName] = useState('');
  const [financialYearCode, setFinancialYearCode] = useState('');
  const [budgetTermId, setBudgetTermId] = useState('1');
  const [showExtraSections, setShowExtraSections] = useState(false);

  // account_id list per section, and account_id -> { term_id: rawValue }
  const [selectedIds, setSelectedIds] = useState({});
  const [amounts, setAmounts] = useState({});
  const [pickerSection, setPickerSection] = useState(null);
  const [formError, setFormError] = useState('');


  useEffect(() => {
    document.title = 'New Budget';
    budgetService
      .getBBudgetmasterData()
      .then(response => {
        const data = normalizeMasterData(response?.data);
        setMasterData(data);
        setFinancialYearCode(String(data.financial_year?.[0]?.financial_year_code ?? ''));
        setBudgetTermId(String(data.budget_term?.[0]?.budget_term_id ?? 1));
      })
      .catch(err => {
        console.error(err);
        logicContext.showToast('Failed to load budget master data', 'error');
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const periods = useMemo(() => getBudgetPeriods(budgetTermId), [budgetTermId]);

  const accountsById = useMemo(() => {
    const map = new Map();
    masterData.account_head_info.forEach(account => map.set(account.account_id, account));
    return map;
  }, [masterData.account_head_info]);

  const accountsBySection = useMemo(() => {
    const groups = {};
    masterData.account_head_info.forEach(account => {
      const key = categorizeAccount(account);
      if (!groups[key]) groups[key] = [];
      groups[key].push(account);
    });
    Object.values(groups).forEach(list =>
      list.sort((a, b) => (a.account_name || '').localeCompare(b.account_name || ''))
    );
    return groups;
  }, [masterData.account_head_info]);


  const visibleSections = useMemo(() => {
    if (!showExtraSections) return CORE_SECTIONS;
    return [
      ...CORE_SECTIONS,
      ...EXTRA_SECTIONS.filter(section => accountsBySection[section.key]?.length),
    ];
  }, [showExtraSections, accountsBySection]);


  // the meaning of a column changes with the term (Jan is not Q1), so amounts
  // captured under the old term cannot be carried over
  const onBudgetTermChange = value => {
    const hasAmounts = Object.values(amounts).some(row =>
      Object.values(row || {}).some(amount => Number(amount) !== 0)
    );
    setBudgetTermId(value);
    if (hasAmounts) {
      setAmounts({});
      logicContext.showToast('Amounts were cleared because the budget period changed', 'info');
    }
  };


  const onPickerConfirm = accountIds => {
    setSelectedIds(prev => ({ ...prev, [pickerSection.key]: accountIds }));
    setPickerSection(null);
  };


  const removeAccount = (sectionKey, accountId) => {
    setSelectedIds(prev => ({
      ...prev,
      [sectionKey]: (prev[sectionKey] || []).filter(id => id !== accountId),
    }));
    setAmounts(prev => {
      const next = { ...prev };
      delete next[accountId];
      return next;
    });
  };


  const setAmount = (accountId, termId, value) => {
    setAmounts(prev => ({
      ...prev,
      [accountId]: { ...prev[accountId], [termId]: value },
    }));
  };


  // copy the first captured value into every remaining column of the row
  const fillRowAcross = accountId => {
    const row = amounts[accountId] || {};
    const source = periods.map(period => row[period.term_id]).find(value => value !== undefined && value !== '');
    if (source === undefined) return;

    const filled = {};
    periods.forEach(period => {
      filled[period.term_id] = source;
    });
    setAmounts(prev => ({ ...prev, [accountId]: filled }));
  };


  const rowTotal = useCallback(
    accountId => {
      const row = amounts[accountId] || {};
      return periods.reduce((sum, period) => sum + (Number(row[period.term_id]) || 0), 0);
    },
    [amounts, periods]
  );


  const budgetLines = useMemo(() => {
    const lines = [];
    visibleSections.forEach(section => {
      (selectedIds[section.key] || []).forEach(accountId => {
        periods.forEach(period => {
          lines.push({
            account_id: accountId,
            term_id: period.term_id,
            amount: Number(amounts[accountId]?.[period.term_id]) || 0,
          });
        });
      });
    });
    return lines;
  }, [visibleSections, selectedIds, periods, amounts]);


  const grandTotal = useMemo(
    () => budgetLines.reduce((sum, line) => sum + line.amount, 0),
    [budgetLines]
  );


  const onSubmit = async event => {
    event.preventDefault();
    setFormError('');

    if (!budgetName.trim()) return setFormError('Budget name is required');
    if (!financialYearCode) return setFormError('Fiscal year is required');
    if (!budgetTermId) return setFormError('Budget period is required');
    if (!budgetLines.length) return setFormError('Add at least one account to the budget');
    if (!budgetLines.some(line => line.amount !== 0)) {
      return setFormError('Enter an amount for at least one account');
    }

    const result = await myPromiseModal({
      title: 'New Budget',
      body: `Create the budget "${budgetName.trim()}"?`,
      showInput: false,
    });
    if (!result?.decision) return;

    setSaving(true);
    try {
      const payload = {
        financial_year_code: Number(financialYearCode),
        budget_term_id: Number(budgetTermId),
        budget_name: budgetName.trim(),
        xml_budget: buildBudgetXml(budgetLines),
        option: 1,
      };

      const response = await budgetService.submitBudget(payload);
      const { message_text, message_id } =
        response.data?.[0] || response.data?.result_status?.[0] || {};

      if (message_id === 1) {
        logicContext?.showToast?.(message_text || 'Budget created', 'success');
        sessionStorage.setItem('reloadPage', 'true');
        navigate(-1);
      } else {
        logicContext?.showToast?.(message_text || 'Failed to create budget', 'error');
      }
    } catch (err) {
      console.error('Failed to save budget', err);
      logicContext?.showToast?.('Failed to save budget', 'error');
    } finally {
      setSaving(false);
    }
  };


  const yearLabel = useMemo(() => {
    const year = masterData.financial_year.find(
      item => String(item.financial_year_code) === String(financialYearCode)
    );
    return year?.financial_year_name || '';
  }, [masterData.financial_year, financialYearCode]);


  return (
    <section className="mt-2 px-3 pb-12">
      <div className="mx-auto max-w-[1600px]">
        <form onSubmit={onSubmit} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-gray-200 px-6 py-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">Budget</p>
              <h1 className="text-lg font-bold text-gray-900">New Budget</h1>
            </div>
          </div>

          {isLoading && <DelgroProgressBar />}

          {/* Budget header fields */}
          <div className="space-y-5 border-b border-gray-100 px-6 py-6">
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Name<span className="ml-0.5 text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={budgetName}
                  onChange={e => setBudgetName(e.target.value)}
                  maxLength={100}
                  placeholder="e.g. Operating Budget 2026"
                  className={inputCls}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Fiscal Year<span className="ml-0.5 text-red-500">*</span>
                </label>
                <select
                  value={financialYearCode}
                  onChange={e => setFinancialYearCode(e.target.value)}
                  className={selectCls}
                >
                  <option value="">Select fiscal year</option>
                  {masterData.financial_year.map(year => (
                    <option key={year.financial_year_code} value={year.financial_year_code}>
                      {year.financial_year_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Budget Period<span className="ml-0.5 text-red-500">*</span>
                </label>
                <select
                  value={budgetTermId}
                  onChange={e => onBudgetTermChange(e.target.value)}
                  className={selectCls}
                >
                  <option value="">Select budget period</option>
                  {masterData.budget_term.map(term => (
                    <option key={term.budget_term_id} value={term.budget_term_id}>
                      {term.budget_term_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {yearLabel && (
              <p className="text-xs text-gray-400">
                Amounts are captured per {periods.length === 1 ? 'year' : `period (${periods.length} columns)`} for {yearLabel}.
              </p>
            )}
          </div>

          {/* Account sections */}
          <div className="space-y-6 px-6 py-6">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
              Income and Expense Accounts
            </h2>

            {visibleSections.map(section => (
              <BudgetSection
                key={section.key}
                section={section}
                periods={periods}
                accountsById={accountsById}
                selectedIds={selectedIds[section.key] || []}
                amounts={amounts}
                onAddAccounts={() => setPickerSection(section)}
                onRemoveAccount={accountId => removeAccount(section.key, accountId)}
                onAmountChange={setAmount}
                onFillAcross={fillRowAcross}
                rowTotal={rowTotal}
              />
            ))}

            <button
              type="button"
              onClick={() => setShowExtraSections(prev => !prev)}
              className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              <Plus className={`h-4 w-4 transition-transform ${showExtraSections ? 'rotate-45' : ''}`} />
              {showExtraSections ? 'Remove' : 'Include'} Asset, Liability, and Equity Accounts in Budget
            </button>
          </div>

          {/* Footer */}
          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-gray-200 bg-gray-50 px-6 py-4">
            <div className="text-sm text-gray-600">
              Budget total:{' '}
              <span className="font-bold tabular-nums text-gray-800">{formatCustomNumber(grandTotal)}</span>
            </div>

            <div className="flex items-center gap-3">
              {formError && <span className="text-sm text-red-500">{formError}</span>}
              <button
                type="submit"
                disabled={saving || isLoading}
                className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Create Budget'}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-100"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>

      {pickerSection && (
        <AccountPickerModalShared
          title={`Add ${pickerSection.title}`}
          accounts={accountsBySection[pickerSection.key] || []}
          selectedIds={selectedIds[pickerSection.key] || []}
          onCancel={() => setPickerSection(null)}
          onConfirm={onPickerConfirm}
        />
      )}
    </section>
  );
}


/* --------------------------------------------------------------- section -- */

function BudgetSection({
  section,
  periods,
  accountsById,
  selectedIds,
  amounts,
  onAddAccounts,
  onRemoveAccount,
  onAmountChange,
  onFillAcross,
  rowTotal,
}) {
  const periodTotals = periods.map(period =>
    selectedIds.reduce(
      (sum, accountId) => sum + (Number(amounts[accountId]?.[period.term_id]) || 0),
      0
    )
  );
  const sectionTotal = periodTotals.reduce((sum, amount) => sum + amount, 0);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-700">{section.title}</h3>
        <button
          type="button"
          onClick={onAddAccounts}
          className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Accounts
        </button>
      </div>

      {selectedIds.length === 0 ? (
        <button
          type="button"
          onClick={onAddAccounts}
          className="w-full rounded-lg border border-dashed border-gray-300 px-4 py-4 text-sm text-blue-600 transition hover:border-blue-300 hover:bg-blue-50/40"
        >
          Add Accounts
        </button>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left font-semibold">Account</th>
                {periods.map(period => (
                  <th key={period.term_id} className="whitespace-nowrap px-2 py-3 text-right font-semibold">
                    {period.label}
                  </th>
                ))}
                <th className="px-4 py-3 text-right font-semibold">Total</th>
                <th className="w-10 px-2 py-3" />
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {selectedIds.map(accountId => {
                const account = accountsById.get(accountId);
                return (
                  <tr key={accountId} className="hover:bg-gray-50">
                    <td className="sticky left-0 z-10 bg-white px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div className="min-w-0">
                          <div className="truncate font-medium text-gray-800">
                            {account?.account_name || `Account ${accountId}`}
                          </div>
                          <div className="truncate text-xs text-gray-400">
                            {account?.account_type_name}
                          </div>
                        </div>
                        {periods.length > 1 && (
                          <button
                            type="button"
                            onClick={() => onFillAcross(accountId)}
                            title="Copy the first amount across all periods"
                            className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-blue-600"
                          >
                            <ChevronsRight className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>

                    {periods.map(period => (
                      <td key={period.term_id} className="px-1 py-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={amounts[accountId]?.[period.term_id] ?? ''}
                          onChange={e => onAmountChange(accountId, period.term_id, e.target.value)}
                          className="w-24 rounded border border-gray-200 px-2 py-1.5 text-right text-sm tabular-nums outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                      </td>
                    ))}

                    <td className="whitespace-nowrap px-4 py-2 text-right font-semibold tabular-nums text-gray-800">
                      {formatCustomNumber(rowTotal(accountId))}
                    </td>

                    <td className="px-2 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => onRemoveAccount(accountId)}
                        title="Remove account"
                        className="flex h-7 w-7 items-center justify-center rounded text-gray-400 hover:bg-red-50 hover:text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>

            <tfoot className="border-t border-gray-200 bg-gray-50 text-sm font-semibold text-gray-800">
              <tr>
                <td className="sticky left-0 z-10 bg-gray-50 px-4 py-3">Total</td>
                {periodTotals.map((amount, index) => (
                  <td key={periods[index].term_id} className="whitespace-nowrap px-2 py-3 text-right tabular-nums">
                    {formatCustomNumber(amount)}
                  </td>
                ))}
                <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">
                  {formatCustomNumber(sectionTotal)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

export default AddBudgetPagePage;
